import React, { useEffect, useRef } from 'react';
import ShiftCard from './ShiftCard';
import FindStoreCard from './FindStoreCard';
import { computeDistance } from '../../helpers/ShiftUtils.js';
import { readFindCache } from '../../helpers/findCache.js';
import './MapShifts.scss';

const MapShiftItem = ({ shift, state, location, requestedShiftList }) => {
  const distance = computeDistance(location.coordinates, shift.address.coordinates, 'kilometers')
  return (
    <div className={'map-shift-position'} id={shift._id} data-id={shift.storeId} >
      <ShiftCard 
        shift={shift} 
        distance={distance} 
        isListView={false}
        currentState={state}
        location={location}
        requestedShiftList={requestedShiftList}
        />
    </div>
  )
}

const MapStoreItem = ({store, setHighlightedStore, map, state, mapCenter, location, setViewStoreShifts, setSelectedStore, navigate, shiftList}) => {
  const distance = computeDistance(mapCenter !== null ? mapCenter : location.coordinates, store.address.coordinates, 'kilometers')
  const highlightIcon = (storeId) => {
    document.getElementById(storeId).scrollIntoView();
    setHighlightedStore(storeId);
    map.current.setLayoutProperty('stores', 'icon-image', ['match', ['get', 'storeId'], storeId, 'active-map-icon', 'map-icon'])
  }
  
  return (
    <div className={'map-shift-position'} id={store._id} data-id={store._id} onClick={() => highlightIcon(store._id)}>
      <FindStoreCard 
        store={store} 
        storeId={store._id} 
        currentState={state} 
        distance={distance} 
        shiftList={shiftList}
        setViewStoreShifts={setViewStoreShifts}
        setSelectedStore={setSelectedStore}
        isListView={false}
      />
    </div>
  )
}

const MapShifts = ({orderedMapShifts, orderedMapStores, setHighlightedStore, setHighlightedShift, map, jobView, state, mapCenter, location, setViewStoreShifts, setSelectedStore, viewStoreShifts, requestedShiftList, navigate, includeEmptyStores}) => {
  const filteredMapStores = orderedMapStores.filter((store => includeEmptyStores || store.shifts.length > 0));
  const restoredFocusId = useRef(null);

  useEffect(() => {
    if (!jobView) return;
    const { focusShiftId } = readFindCache();
    if (!focusShiftId) return;
    // Only restore once per focus id — re-running on carousel updates snaps the user back.
    if (restoredFocusId.current === focusShiftId) return;

    let cancelled = false;
    const tryScroll = () => {
      if (cancelled) return true;
      const el = document.getElementById(focusShiftId);
      if (!el) return false;
      el.scrollIntoView();
      restoredFocusId.current = focusShiftId;
      return true;
    };

    if (tryScroll()) return undefined;
    const timeoutId = setTimeout(tryScroll, 50);
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [jobView, orderedMapShifts]);

  useEffect(() => {
    if (!map || !map.current) return;

    const storesForMap = orderedMapStores.filter((store) => includeEmptyStores || store.shifts.length > 0);
    const mapStoreObj = storesForMap.reduce((sum, count) => ({...sum, [String(count._id)]: count.shifts.length}), {});
    const mapStoreCoords = storesForMap.reduce((acc, store) => ({
      ...acc,
      [String(store._id)]: { coordinates: store.address.coordinates, isTopRated: store.isTopRated }
    }), {});

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const storeId = String(entry.target.dataset.id);
        const storeCoords = mapStoreCoords[storeId];
        if (!storeCoords || !storeCoords.coordinates) return;

        if (jobView) {
          setHighlightedShift(storeId);
          if (storeCoords.isTopRated && map.current.getLayer('topRatedShifts')) {
            map.current.setLayoutProperty('topRatedShifts', 'icon-image', ['match', ['get', 'storeId'], storeId, 'active-map-icon', 'top-rated-map-icon']);
            map.current.setLayoutProperty('shifts', 'icon-image', 'map-icon');
          } else if (map.current.getLayer('shifts')) {
            map.current.setLayoutProperty('shifts', 'icon-image', ['match', ['get', 'storeId'], storeId, 'active-map-icon', 'map-icon']);
            map.current.setLayoutProperty('topRatedShifts', 'icon-image', 'top-rated-map-icon');
          }
          map.current.flyTo({
            center: [storeCoords.coordinates.longitude, storeCoords.coordinates.latitude],
            essential: true
          });
        } else {
          setHighlightedStore(storeId);
          if (mapStoreObj[storeId] === 0 && map.current.getLayer('emptyStores')) {
            map.current.setLayoutProperty('emptyStores', 'icon-image', ['match', ['get', 'storeId'], storeId, 'active-map-icon', 'inactive-map-icon']);
            map.current.setLayoutProperty('stores', 'icon-image', 'map-icon');
            map.current.setLayoutProperty('topRatedStores', 'icon-image', 'top-rated-map-icon');
          } else if (storeCoords.isTopRated && map.current.getLayer('topRatedStores')) {
            map.current.setLayoutProperty('topRatedStores', 'icon-image', ['match', ['get', 'storeId'], storeId, 'active-map-icon', 'top-rated-map-icon']);
            map.current.setLayoutProperty('emptyStores', 'icon-image', 'inactive-map-icon');
            map.current.setLayoutProperty('stores', 'icon-image', 'map-icon');
          } else if (map.current.getLayer('stores')) {
            map.current.setLayoutProperty('stores', 'icon-image', ['match', ['get', 'storeId'], storeId, 'active-map-icon', 'map-icon']);
            map.current.setLayoutProperty('emptyStores', 'icon-image', 'inactive-map-icon');
            map.current.setLayoutProperty('topRatedStores', 'icon-image', 'top-rated-map-icon');
          }
          map.current.flyTo({
            center: [storeCoords.coordinates.longitude, storeCoords.coordinates.latitude],
            essential: true
          });
        }
      });
    }, { threshold: 1 });

    const cards = document.querySelectorAll('.map-shift-position');
    cards.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, [
    includeEmptyStores,
    jobView,
    map,
    orderedMapShifts,
    orderedMapStores,
    setHighlightedShift,
    setHighlightedStore,
  ]);

  return (
    <section className={'map-shift-container'}>
      <div className={'map-shift-carousel'}>
        { jobView && orderedMapShifts.map(shift => 
          <MapShiftItem 
            shift={shift} 
            state={state}
            location={location}
            requestedShiftList={requestedShiftList}
            key={`mapshift-${shift._id}`}
          />
        )}
        {!jobView && filteredMapStores.map(store => 
          <MapStoreItem 
            store={store} 
            storeId={store._id}
            setHighlightedStore={setHighlightedStore} 
            shiftList={orderedMapShifts.filter(shift => shift.storeId === store._id)}
            map={map}
            state={state}
            mapCenter={mapCenter}
            location={location}
            setViewStoreShifts={setViewStoreShifts}
            setSelectedStore={setSelectedStore}
            navigate={navigate}
            key={`mapstore-${store._id}`}
          />
        )}
      </div>
    </section>
  )
}

export default MapShifts;
